
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Loader2, MapPin, Phone, Calendar, Star, Share2 } from 'lucide-react';
import StudioBookingPanel from "@/components/booking/StudioBookingPanel";
import { StudioServices } from "@/components/studio/StudioServices";
import { StudioLocation } from "@/components/studio/StudioLocation";
import { ReviewsList } from "@/components/reviews/ReviewsList";
import { useStudioBySlug } from '@/hooks/use-studio-by-slug';
import { PageSEO } from '@/components/SEO/PageSEO';
import { StudioSchema } from '@/components/SEO/StudioSchema';
import { StudioHeader } from '@/components/studio/StudioHeader';
import { Provider } from '@/types/model';
import { motion } from 'framer-motion';
import { RecommendedStudios } from '@/components/studio/RecommendedStudios';
import { Studio } from '@/hooks/use-studios';
import { useBooking } from '@/contexts/BookingContext';

// Function to generate opening hours for schema
const getDefaultOpeningHours = () => [
  { dayOfWeek: "Monday", opens: "09:00", closes: "18:00" },
  { dayOfWeek: "Tuesday", opens: "09:00", closes: "18:00" },
  { dayOfWeek: "Wednesday", opens: "09:00", closes: "18:00" },
  { dayOfWeek: "Thursday", opens: "09:00", closes: "18:00" },
  { dayOfWeek: "Friday", opens: "09:00", closes: "18:00" },
  { dayOfWeek: "Saturday", opens: "10:00", closes: "16:00" },
  { dayOfWeek: "Sunday", opens: "00:00", closes: "00:00" }
];

export default function StudioPublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const { studio, loading, error } = useStudioBySlug(slug);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('booking');
  const { selectedService, setSelectedService } = useBooking();
  
  // Convert StudioDetail to Studio type for consistent interface
  const studioData: Studio | null = studio ? {
    id: studio.id,
    name: studio.name,
    description: studio.description,
    address: studio.location?.address,
    slug: studio.id, // Use ID as slug if not available
    phone: studio.location?.phone,
    is_registered: studio.is_featured,
    rating_avg: studio.rating || 0,
    rating_count: 0,
    geo_location: {
      latitude: studio.location?.latitude || 0,
      longitude: studio.location?.longitude || 0
    }
  } : null;
  
  // Generate a location string for SEO purposes
  const locationString = studioData?.address ? studioData.address.split(',')[0] : 'deiner Stadt';
  
  // Handle sharing studio page
  const handleShare = async () => {
    const shareData = {
      title: `${studioData?.name} auf CHRONA`,
      text: `Buche deinen Termin bei ${studioData?.name} über CHRONA.`,
      url: window.location.href
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback if Web Share API is not available
        navigator.clipboard.writeText(window.location.href);
        toast({
          title: "Link kopiert",
          description: "Der Link wurde in die Zwischenablage kopiert.",
        });
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };
  
  // Handler for booking a service
  const handleBookService = (serviceId: string) => {
    console.log(`Booking service ${serviceId} from studio ${studio?.id}`);
    // Navigate to booking page with pre-selected studio and service
    navigate(`/booking/${studio?.id}?service=${serviceId}`);
  };
  
  // If loading, show loading spinner
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C6E49]" />
      </div>
    );
  }
  
  // If no studio found, show error and update SEO to prevent indexing
  if (!studioData) {
    return (
      <div className="container mx-auto px-4 py-16">
        <PageSEO 
          title="Studio nicht gefunden" 
          description="Das gesuchte Studio konnte nicht gefunden werden." 
          noindex={true}
        />
        <Card>
          <CardHeader>
            <CardTitle>Studio nicht gefunden</CardTitle>
            <CardDescription>
              Das gesuchte Studio konnte nicht gefunden werden.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/">
              <Button>Zurück zur Startseite</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Convert studio to Provider format for StudioBookingPanel
  const studioAsProvider: Provider = {
    id: studioData.id,
    name: studioData.name,
    branche: "friseur", // Default value
    adresse: studioData.address || "",
    standort: {
      latitude: studioData.geo_location?.latitude || 0,
      longitude: studioData.geo_location?.longitude || 0
    },
    beschreibung: studioData.description || "",
    zeiten: {},
    mitarbeiter: [],
    aktivierte_addons: [],
    limit_buchungen: 0,
    branding: false,
    abo_status: "free",
    imageUrl: ""
  };
  
  // Generate dynamic meta description based on studio data
  const metaDescription = studioData.description || 
    `${studioData.name} in ${locationString} bietet professionelle Dienstleistungen. Buche deinen Termin einfach online mit CHRONA.`;
    
  // Create structured data for the studio
  const studioStructuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": studioData.name,
    "description": metaDescription,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": studioData.address?.split(',')[0] || "",
      "addressLocality": locationString,
      "addressCountry": "DE"
    },
    "telephone": studioData.phone || "",
    "url": `https://www.chrona.it/studio/${studioData.slug || studioData.id}`,
    "image": `https://chrona.it/studio-images/${studioData.id}.jpg`,
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": studioData.geo_location?.latitude,
      "longitude": studioData.geo_location?.longitude
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": studioData.rating_avg,
      "ratingCount": studioData.rating_count
    },
    "openingHoursSpecification": getDefaultOpeningHours().map(day => ({
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": day.dayOfWeek,
      "opens": day.opens,
      "closes": day.closes
    }))
  };
  
  return (
    <div className="bg-[#212121] min-h-screen">
      {/* Enhanced SEO Component */}
      <PageSEO 
        title={`${studioData.name} - Termin buchen`}
        description={metaDescription}
        keywords={`${studioData.name}, Terminbuchung, ${locationString}, Beauty, Wellness, Friseur, Kosmetik, online buchen`}
        canonicalPath={`/studio/${studioData.slug || studioData.id}`}
        ogImage={`https://chrona.it/studio-images/${studioData.id}.jpg`}
        ogType="business.business"
        twitterCard="summary_large_image"
        twitterSite="@chrona_app"
        structuredData={studioStructuredData}
      />
      
      {/* Studio content */}
      <div className="container mx-auto px-4 py-8">
        {/* Studio Header */}
        <div className="mb-8">
          <StudioHeader studio={studioData} />
          
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to={`/booking/${studioData.id}`}>
              <Button className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Termin buchen
              </Button>
            </Link>
            
            <Button 
              variant="outline" 
              onClick={handleShare}
              className="flex items-center gap-2"
            >
              <Share2 className="h-4 w-4" />
              Teilen
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="mb-8"
        >
          <TabsList className="mb-6">
            <TabsTrigger value="booking">Terminbuchung</TabsTrigger>
            <TabsTrigger value="services">Dienstleistungen</TabsTrigger>
            <TabsTrigger value="location">Standort</TabsTrigger>
            <TabsTrigger value="reviews">Bewertungen</TabsTrigger>
          </TabsList>
          
          <TabsContent value="booking">
            <Card className="border-t-0 rounded-t-none">
              <CardContent className="pt-6">
                <StudioBookingPanel 
                  provider={studioAsProvider}
                  selectedService={selectedService}
                  onServiceSelected={(service) => setSelectedService(service)} 
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="services">
            <Card className="border-t-0 rounded-t-none">
              <CardContent className="pt-6">
                <StudioServices studioId={studioData.id} onBookService={handleBookService} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="location">
            <Card className="border-t-0 rounded-t-none">
              <CardContent className="pt-6">
                <StudioLocation studio={studioData} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="reviews">
            <Card className="border-t-0 rounded-t-none">
              <CardContent className="pt-6">
                <ReviewsList studioId={studioData.id} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Recommended Studios Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-12 mb-8"
        >
          <h2 className="text-2xl font-semibold mb-6">Empfohlene Studios in deiner Nähe</h2>
          <RecommendedStudios 
            currentStudioId={studioData.id} 
            location={studioData.geo_location} 
          />
        </motion.div>
      </div>
    </div>
  );
}
